import {
  ACTIVE_PIPELINE_STATUSES,
  BreifInfluencerV2StatusEnum,
} from "@entities/brief/lib/constants";
import { IBriefInfluencerSearchRequestV2 } from "@shared/lib/types/briefInfluencerV2";
import {
  CampaignBriefStage,
  getCampaignBriefStageSearchParams,
} from "./campaignBriefStage";

export type InfluencerColumnStatusFilterValue =
  | "creatorDeclined"
  | "brandDeclined"
  | "onboardingPending"
  | "contactNotFound"
  | "deliverablesPending"
  | "awaitingCommercials"
  | "awaitingBrandPrice"
  | "purchased"
  | "confirmationSent"
  | "confirmationPending";

export const INFLUENCER_COLUMN_STATUS_FILTER_OPTIONS: {
  label: string;
  value: InfluencerColumnStatusFilterValue;
}[] = [
  { label: "Creator Declined", value: "creatorDeclined" },
  { label: "Brand Declined", value: "brandDeclined" },
  { label: "Onboarding Pending", value: "onboardingPending" },
  { label: "Contact not found", value: "contactNotFound" },
  { label: "Deliverables Pending", value: "deliverablesPending" },
  { label: "Awaiting Commercials", value: "awaitingCommercials" },
  { label: "Awaiting Brand Price", value: "awaitingBrandPrice" },
  { label: "Purchased", value: "purchased" },
  { label: "Confirmation Mail sent", value: "confirmationSent" },
  { label: "Confirmation Pending", value: "confirmationPending" },
];

type InfluencerColumnStatusSearchParams = Pick<
  IBriefInfluencerSearchRequestV2,
  | "status"
  | "statuses"
  | "isCreatorDeclined"
  | "isBrandDeclined"
  | "isCreatorOnboardPending"
  | "isPrimaryContactAdded"
  | "isDeliverablesPending"
  | "isInitialInfluencerCostPending"
  | "isInitialBrandCostPending"
  | "isConfirmationSent"
  | "isShortlisted"
>;

const withStageShortlist = (
  params: InfluencerColumnStatusSearchParams,
  stage: CampaignBriefStage
): InfluencerColumnStatusSearchParams => {
  if (stage === CampaignBriefStage.Purchased) {
    return params;
  }
  return {
    ...params,
    isShortlisted: stage === CampaignBriefStage.Shortlisted,
  };
};

export function getInfluencerColumnStatusSearchParams(
  filter: InfluencerColumnStatusFilterValue | undefined,
  activeStage: CampaignBriefStage
): InfluencerColumnStatusSearchParams {
  const stageParams = getCampaignBriefStageSearchParams(activeStage);

  switch (filter) {
    case "creatorDeclined":
      return withStageShortlist(
        { isCreatorDeclined: true, statuses: stageParams.statuses },
        activeStage
      );
    case "brandDeclined":
      return withStageShortlist(
        { isBrandDeclined: true, statuses: stageParams.statuses },
        activeStage
      );
    case "onboardingPending":
      return withStageShortlist(
        {
          statuses: ACTIVE_PIPELINE_STATUSES,
          isCreatorOnboardPending: true,
          isCreatorDeclined: false,
          isBrandDeclined: false,
        },
        activeStage
      );
    case "contactNotFound":
      return withStageShortlist(
        {
          statuses: ACTIVE_PIPELINE_STATUSES,
          isPrimaryContactAdded: false,
          isCreatorDeclined: false,
          isBrandDeclined: false,
        },
        activeStage
      );
    case "deliverablesPending":
      return withStageShortlist(
        {
          statuses: ACTIVE_PIPELINE_STATUSES,
          isDeliverablesPending: true,
          isCreatorDeclined: false,
          isBrandDeclined: false,
        },
        activeStage
      );
    case "awaitingCommercials":
      return withStageShortlist(
        {
          statuses: ACTIVE_PIPELINE_STATUSES,
          isInitialInfluencerCostPending: true,
          isCreatorDeclined: false,
          isBrandDeclined: false,
        },
        activeStage
      );
    case "awaitingBrandPrice":
      return withStageShortlist(
        {
          statuses: ACTIVE_PIPELINE_STATUSES,
          isInitialBrandCostPending: true,
          isCreatorDeclined: false,
          isBrandDeclined: false,
        },
        activeStage
      );
    case "purchased":
      return {
        statuses: [BreifInfluencerV2StatusEnum.Purchased],
        isCreatorDeclined: false,
        isBrandDeclined: false,
      };
    case "confirmationSent":
      return withStageShortlist(
        { isConfirmationSent: true, statuses: stageParams.statuses },
        activeStage
      );
    case "confirmationPending":
      return withStageShortlist(
        { isConfirmationSent: false, statuses: stageParams.statuses },
        activeStage
      );
    default:
      return stageParams;
  }
}

export function getShowDeclinedCreatorsSearchParams(
  showDeclinedCreators: boolean,
  activeStage: CampaignBriefStage
): InfluencerColumnStatusSearchParams {
  if (!showDeclinedCreators) {
    return {};
  }

  return {
    statuses: [BreifInfluencerV2StatusEnum.Deleted],
    ...(activeStage === CampaignBriefStage.Purchased
      ? { status: BreifInfluencerV2StatusEnum.Purchased }
      : { isShortlisted: activeStage === CampaignBriefStage.Shortlisted }),
  };
}
